<template>
  <div class="page">
    <div class="page-header">
      <div>
        <h1 class="page-title">Cobranças</h1>
        <p class="page-help">Quem já pagou, quem está devendo e quanto falta entrar.</p>
      </div>
    </div>

    <SemEventoBanner v-if="!eventos.length && !carregandoEventos" />

    <div v-else class="controles card">
      <div class="controles-linha">
        <select class="select" v-model="eventoId" @change="carregar()" aria-label="Evento">
          <option v-for="e in eventos" :key="e.id" :value="e.id">{{ e.nome }}</option>
        </select>
        <div class="busca-wrap">
          <input class="input" v-model="busca" placeholder="Buscar inscrito..."
                 aria-label="Buscar inscrito" @keyup.enter="carregar()" @input="buscarComPausa()" />
          <button v-if="busca" type="button" class="btn-limpar" aria-label="Limpar busca"
                  @click="busca = ''; carregar()">✕</button>
        </div>
      </div>
      <div v-if="eventoId" class="tabs" role="tablist">
        <button v-for="s in SITUACOES" :key="s.valor" class="tab" role="tab"
                :aria-selected="situacao === s.valor"
                :class="{ active: situacao === s.valor }"
                @click="situacao = s.valor; carregar()">
          {{ s.rotulo }}
          <span v-if="contagem(s.valor) !== null" class="tab-badge">{{ contagem(s.valor) }}</span>
        </button>
      </div>
    </div>

    <!-- Totalizadores: somam exatamente as linhas exibidas abaixo -->
    <div v-if="totais && !loading" class="totais">
      <div class="total-card card">
        <span class="total-label">Arrecadado</span>
        <strong class="total-valor valor-ok">{{ formatMoney(totais.arrecadado) }}</strong>
      </div>
      <div class="total-card card">
        <span class="total-label">A receber</span>
        <strong class="total-valor">{{ formatMoney(totais.aReceber) }}</strong>
      </div>
      <div class="total-card card">
        <span class="total-label">Em atraso</span>
        <strong class="total-valor" :class="totais.emAtraso > 0 ? 'valor-alerta' : ''">
          {{ formatMoney(totais.emAtraso) }}
        </strong>
      </div>
      <div class="total-card card">
        <span class="total-label">Inscritos</span>
        <strong class="total-valor">
          {{ totais.inscritos }}
          <small v-if="totais.atrasados">· {{ totais.atrasados }} atrasado(s)</small>
        </strong>
      </div>
    </div>

    <SkeletonLoader v-if="loading" />
    <EmptyState v-else-if="!cobrancas.length" icon="📋" title="Nada por aqui"
                :description="situacao || busca ? 'Nenhum inscrito com esse filtro.' : 'Esse evento ainda não tem cobranças.'" />

    <div v-else class="lista">
      <div v-for="c in cobrancas" :key="c.grupoId" class="cobranca card" :class="`sit-${c.situacao}`">
        <div class="cob-topo">
          <div class="cob-quem">
            <div class="cob-nome-linha">
              <strong class="cob-nome">{{ c.nome }}</strong>
              <span v-if="c.tamanho > 1" class="cob-grupo">grupo de {{ c.tamanho }}</span>
            </div>
            <span class="cob-plano">{{ c.plano }}</span>
          </div>
          <span class="chip" :class="`chip-${c.situacao}`">{{ rotuloSituacao(c.situacao) }}</span>
        </div>

        <!-- Régua e números lado a lado: em linhas separadas, cada card passava
             de 280px e oito inscritos viravam uma rolagem longa. -->
        <div class="cob-corpo">
          <div v-if="c.carne" class="regua" :aria-label="`${c.pagas} de ${c.total} parcelas pagas`">
            <button v-for="p in c.parcelas" :key="p.id"
                    type="button"
                    class="parcela"
                    :class="[`p-${p.situacao}`, { 'p-manual': p.manual, clicavel: podeLancar && p.situacao !== 'paga' }]"
                    :title="tituloParcela(p)"
                    :disabled="!podeLancar || p.situacao === 'paga'"
                    @click="abrirLancamento(c, p)">
              <span class="parcela-num">{{ p.numero }}</span>
            </button>
          </div>

          <div class="cob-numeros">
            <span class="cob-progresso"><strong>{{ c.pagas }}/{{ c.total }}</strong></span>
            <span class="cob-valores">
              <span class="v-pago">{{ formatMoney(c.valorPago) }}</span>
              <span v-if="c.valorEmAberto > 0" class="v-aberto">+ {{ formatMoney(c.valorEmAberto) }} em aberto</span>
            </span>
            <span v-if="c.situacao === 'atrasado'" class="cob-atraso">
              atrasada desde {{ formatDate(primeiraVencida(c)) }}
            </span>
            <span v-else-if="c.proximoVencimento" class="cob-vence">
              vence {{ formatDate(c.proximoVencimento) }}
            </span>
          </div>

          <button v-if="podeLancar && temLancavel(c)" class="btn-lancar"
                  :title="`Registrar pagamento recebido por fora de ${c.nome}`"
                  @click="abrirLancamento(c)">
            Registrar por fora
          </button>

          <button v-if="podeCobrarWhatsapp && parcelaParaCobrar(c)" class="btn-whatsapp"
                  :disabled="cobrandoWhatsapp === c.grupoId"
                  :title="`Mandar lembrete de cobranca por WhatsApp para ${c.nome}`"
                  @click="cobrarPorWhatsapp(c)">
            {{ cobrandoWhatsapp === c.grupoId ? 'Enviando…' : '📱 Cobrar via WhatsApp' }}
          </button>
        </div>

        <!-- Quem lançou cada pagamento por fora fica visível: sem isso,
             "por que essa parcela está paga se não tem cobrança?" vira arqueologia. -->
        <div v-if="manuais(c).length" class="cob-manuais">
          <div v-for="p in manuais(c)" :key="`m-${p.id}`" class="manual-linha">
            <span class="manual-tag">Parcela {{ p.numero }} por fora</span>
            <span v-if="p.registradoPor" class="manual-quem">— {{ p.registradoPor }}</span>
            <span v-if="p.observacao" class="manual-obs">“{{ p.observacao }}”</span>
          </div>
        </div>

        <div v-if="ultimaCobrancaWhatsapp(c)" class="cob-whatsapp-aviso">
          📱 Cobrado via WhatsApp {{ tempoRelativo(ultimaCobrancaWhatsapp(c).em) }}, por {{ ultimaCobrancaWhatsapp(c).por }}
        </div>
      </div>
    </div>

    <!-- Lançamento manual -->
    <div v-if="lancando" class="modal-overlay" @click.self="fecharLancamento()">
      <div class="modal-card">
        <h2 class="modal-title">Registrar pagamento por fora</h2>
        <p class="modal-sub">{{ lancando.nome }} · {{ lancando.plano }}</p>

        <div class="aviso-manual">
          Este lançamento não passa por gateway nenhum: ele vale pela sua palavra.
          Fica gravado quem registrou, quando e por quê.
        </div>

        <div class="form-row">
          <label class="label">Quais parcelas foram pagas?</label>
          <div class="escolha-parcelas">
            <label v-for="p in lancaveis(lancando)" :key="p.id" class="escolha"
                   :class="{ active: selecionadas.includes(p.id) }">
              <input type="checkbox" :value="p.id" v-model="selecionadas" />
              <span class="escolha-num">Parcela {{ p.numero }}</span>
              <span class="escolha-venc">vence {{ formatDate(p.vencimento) }}</span>
              <span class="escolha-valor">{{ formatMoney(p.valor) }}</span>
            </label>
          </div>
          <p v-if="!lancaveis(lancando).length" class="help-text">
            Nenhuma parcela desta inscrição aceita lançamento manual.
          </p>
        </div>

        <div v-if="selecionadas.length" class="total-lancamento">
          Total a registrar: <strong>{{ formatMoney(totalSelecionado) }}</strong>
          <small v-if="selecionadas.length > 1">({{ selecionadas.length }} parcelas)</small>
        </div>

        <div class="form-row">
          <label class="label">Como o dinheiro foi recebido? *</label>
          <textarea class="input" rows="2" v-model="observacao" maxlength="500"
                    placeholder="Ex.: PIX direto na chave da igreja em 12/10, comprovante com a tesouraria"></textarea>
          <span class="help-text">Obrigatório. É o que responde “por que essa parcela está paga?” daqui a seis meses.</span>
        </div>

        <div v-if="erro" class="error-banner">{{ erro }}</div>

        <div class="modal-actions">
          <button class="btn-secondary" @click="fecharLancamento()">Cancelar</button>
          <button class="btn-primary" :disabled="!podeSalvar || salvando" @click="salvarLancamento()">
            {{ salvando ? 'Registrando...' : 'Registrar recebimento' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { useEventosStore } from '../../stores/eventos'
import {
  getCobrancas, registrarPagamentoManual, cobrarViaWhatsapp, SITUACOES, formatMoney, formatDate, tempoRelativo
} from '../../lib/cobrancas'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import EmptyState from '../../components/ui/EmptyState.vue'
import SemEventoBanner from '../../components/ui/SemEventoBanner.vue'

const ROTULOS = { 'em-dia': 'Em dia', atrasado: 'Atrasado', quitado: 'Quitado' }

export default {
  name: 'CobrancasPage',
  components: { SkeletonLoader, EmptyState, SemEventoBanner },
  data() {
    return {
      eventoId: '',
      situacao: '',
      busca: '',
      cobrancas: [],
      totais: null,
      podeLancar: false,
      loading: false,
      carregandoEventos: true,
      lancando: null,
      selecionadas: [],
      observacao: '',
      salvando: false,
      erro: '',
      buscaTimer: null,
      cobrandoWhatsapp: null, // id da parcela em andamento, para desabilitar so aquele botao
      SITUACOES
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    eventosStore() { return useEventosStore() },
    eventos() {
      return this.eventosStore.eventos.filter(e => e.status !== 'ARQUIVADO')
    },
    totalSelecionado() {
      if (!this.lancando) return 0
      return this.lancando.parcelas
        .filter(p => this.selecionadas.includes(p.id))
        .reduce((s, p) => s + p.valor, 0)
    },
    podeSalvar() {
      return this.selecionadas.length > 0 && this.observacao.trim().length >= 3
    },
    podeCobrarWhatsapp() {
      const role = this.authStore.role
      return role === 'ROOT' || role === 'ADMIN' || role === 'ORGANIZADOR'
    }
  },
  async mounted() {
    await this.eventosStore.fetchAll()
    this.carregandoEventos = false
    if (this.eventos.length) {
      this.eventoId = this.eventos[0].id
      await this.carregar()
    }
  },
  beforeUnmount() {
    clearTimeout(this.buscaTimer)
  },
  methods: {
    formatMoney,
    formatDate,
    rotuloSituacao(s) { return ROTULOS[s] || s },
    /**
     * Contagem na aba, quando ela é derivável do que já veio.
     *
     * Só aparece sem filtro: com filtro ativo a lista traz apenas uma situação,
     * e mostrar "Atrasados 0" enquanto se olha os quitados seria mentira.
     */
    contagem(valor) {
      if (!this.totais || this.situacao !== '') return null
      if (valor === '') return this.totais.inscritos
      if (valor === 'atrasados') return this.totais.atrasados
      if (valor === 'quitados') return this.totais.quitados
      if (valor === 'em-dia') {
        return this.totais.inscritos - this.totais.atrasados - this.totais.quitados
      }
      return null
    },
    /** Buscar enquanto digita, sem uma requisição por tecla. */
    buscarComPausa() {
      clearTimeout(this.buscaTimer)
      this.buscaTimer = setTimeout(() => this.carregar(), 350)
    },
    async carregar() {
      if (!this.eventoId) return
      this.loading = true
      try {
        const d = await getCobrancas(this.eventoId, { situacao: this.situacao, q: this.busca.trim() })
        this.cobrancas = d.cobrancas
        this.totais = d.totais
        this.podeLancar = d.podeLancar
      } catch (err) {
        this.$toast?.open({
          message: err.response?.data?.message || 'Falha ao carregar cobranças',
          type: 'error', position: 'top-right'
        })
      } finally {
        this.loading = false
      }
    },
    /** Parcelas que aceitam lançamento: nem pagas, nem já cobradas no gateway. */
    lancaveis(c) {
      return c ? c.parcelas.filter(p => p.situacao === 'vencida' || p.situacao === 'a-vencer') : []
    },
    temLancavel(c) { return this.lancaveis(c).length > 0 },
    manuais(c) { return c.parcelas.filter(p => p.manual) },
    primeiraVencida(c) {
      const v = c.parcelas.filter(p => p.situacao === 'vencida')
      return v.length ? v[0].vencimento : null
    },
    tituloParcela(p) {
      const base = `Parcela ${p.numero} · ${formatMoney(p.valor)} · vence ${formatDate(p.vencimento)}`
      if (p.situacao === 'paga') {
        return p.manual
          ? `${base} · recebida por fora${p.registradoPor ? ` (${p.registradoPor})` : ''}`
          : `${base} · paga`
      }
      return p.situacao === 'vencida' ? `${base} · VENCIDA` : base
    },
    abrirLancamento(c, parcela = null) {
      if (!this.podeLancar) return
      this.lancando = c
      this.selecionadas = parcela && parcela.situacao !== 'paga' ? [parcela.id] : []
      this.observacao = ''
      this.erro = ''
    },
    fecharLancamento() {
      this.lancando = null
      this.selecionadas = []
      this.observacao = ''
      this.erro = ''
    },
    async salvarLancamento() {
      this.salvando = true
      this.erro = ''
      try {
        const r = await registrarPagamentoManual({
          pagamentoIds: this.selecionadas,
          observacao: this.observacao.trim()
        })
        this.$toast?.open({ message: r.message, type: 'success', position: 'top-right' })
        this.fecharLancamento()
        await this.carregar()
      } catch (err) {
        this.erro = err.response?.data?.message || 'Falha ao registrar o pagamento.'
      } finally {
        this.salvando = false
      }
    },
    /** A cobranca por WhatsApp mais recente entre as parcelas do grupo, se houver. */
    ultimaCobrancaWhatsapp(c) {
      const comRastro = c.parcelas.filter(p => p.ultimaCobrancaWhatsappEm)
      if (!comRastro.length) return null
      const maisRecente = comRastro.sort((a, b) => new Date(b.ultimaCobrancaWhatsappEm) - new Date(a.ultimaCobrancaWhatsappEm))[0]
      return { em: maisRecente.ultimaCobrancaWhatsappEm, por: maisRecente.ultimaCobrancaWhatsappPor }
    },
    /**
     * Parcela elegivel para cobrar: a primeira vencida, senao a primeira a
     * vencer. Mesmo criterio do "Registrar por fora" para achar a parcela
     * certa quando o clique e no grupo, nao numa parcela especifica.
     *
     * `p.invoiceUrl` e obrigatorio: "a-vencer" inclui parcela SCHEDULED do
     * carne (ainda sem cobranca emitida no gateway), e sem o link o backend
     * recusa com 409 PARCELA_NAO_ELEGIVEL. Sem este filtro o botao aparecia
     * pra quase todo grupo em dia e o clique so dava erro — achado da revisao
     * de codigo.
     */
    parcelaParaCobrar(c) {
      const elegiveis = c.parcelas.filter(p => (p.situacao === 'vencida' || p.situacao === 'a-vencer') && p.invoiceUrl)
      return elegiveis.find(p => p.situacao === 'vencida') || elegiveis[0] || null
    },
    async cobrarPorWhatsapp(c, forcar = false) {
      const parcela = this.parcelaParaCobrar(c)
      if (!parcela) {
        this.$toast?.open({ message: 'Nenhuma parcela em aberto para cobrar.', type: 'info', position: 'top-right' })
        return
      }
      this.cobrandoWhatsapp = c.grupoId
      try {
        const r = await cobrarViaWhatsapp(parcela.id, { forcar })
        this.$toast?.open({ message: r.message, type: 'success', position: 'top-right' })
        await this.carregar()
      } catch (err) {
        const body = err.response?.data
        if (body?.code === 'COBRADO_RECENTEMENTE') {
          const quando = tempoRelativo(body.ultimaCobrancaEm)
          const quem = body.ultimaCobrancaPorNome ? `, por ${body.ultimaCobrancaPorNome}` : ''
          const confirmou = confirm(`Essa parcela já foi cobrada por WhatsApp ${quando}${quem}. Cobrar de novo?`)
          if (confirmou) {
            this.cobrandoWhatsapp = null
            await this.cobrarPorWhatsapp(c, true)
            return
          }
        } else {
          this.$toast?.open({ message: body?.message || 'Falha ao cobrar por WhatsApp.', type: 'error', position: 'top-right' })
        }
      } finally {
        this.cobrandoWhatsapp = null
      }
    }
  }
}
</script>

<style scoped>
/* Controles: evento, busca e abas num bloco só. Eram dois cards empilhados,
   e as abas não tinham estilo nenhum — `.tab` é scoped no SaquesPage, então
   aqui saíam como botão nativo, espremidas e vazando por cima do card. */
.controles { padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px; }
.controles-linha { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 10px; }
.busca-wrap { position: relative; display: flex; }
.busca-wrap .input { width: 100%; padding-right: 30px; }
.btn-limpar {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  background: none; border: 0; cursor: pointer; padding: 4px;
  color: var(--color-text-muted); font-size: 13px; line-height: 1;
}
.btn-limpar:hover { color: var(--color-text-secondary); }

.tabs { display: flex; gap: 4px; overflow-x: auto; }
.tab {
  background: none; border: 0; padding: 7px 12px; border-radius: 5px;
  cursor: pointer; font-size: 13px; color: var(--color-text-muted);
  display: flex; align-items: center; gap: 6px; white-space: nowrap;
  font-family: inherit;
}
.tab:hover { background: rgba(0, 0, 0, 0.04); color: var(--color-text-secondary); }
.tab.active { background: var(--color-accent); color: #fff; }
.tab-badge {
  font-size: 11px; font-weight: 700; padding: 1px 6px; border-radius: 8px;
  background: rgba(0, 0, 0, 0.07); color: inherit;
}
.tab.active .tab-badge { background: rgba(255, 255, 255, 0.25); }

.totais { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
.total-card { padding: 12px 14px; display: flex; flex-direction: column; gap: 2px; }
.total-label { font-size: 11px; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.5px; }
.total-valor { font-size: var(--text-lg); font-weight: 700; }
.total-valor small { font-size: 11px; font-weight: 500; color: var(--color-text-secondary); }
.valor-ok { color: #166534; }
.valor-alerta { color: #b91c1c; }

.lista { display: flex; flex-direction: column; gap: 8px; }
.cobranca { padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; }
/* Faixa lateral: dá para varrer a lista e achar os atrasados sem ler os chips. */
.sit-atrasado { border-left: 3px solid var(--color-danger); }
.sit-quitado { border-left: 3px solid var(--color-success); }
.sit-em-dia { border-left: 3px solid transparent; }

.cob-topo { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
.cob-quem { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.cob-nome-linha { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
.cob-nome { font-size: var(--text-base); }
.cob-grupo, .cob-plano { font-size: 12px; color: var(--color-text-secondary); }

/* Régua, números e ação na MESMA linha. Em linhas separadas cada card passava
   de 280px, e oito inscritos viravam uma rolagem longa para uma leitura que
   deveria ser de relance. */
.cob-corpo { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

/* `flex-wrap` em vez de largura fixa: um carnê de 9 tem que caber no celular
   sem espremer o quadradinho até virar risco. */
.regua { display: flex; flex-wrap: wrap; gap: 4px; }
.parcela {
  width: 26px; height: 26px; border-radius: 4px;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
  border: 1px solid transparent; padding: 0;
  font-family: inherit; cursor: default;
}
.parcela.clicavel { cursor: pointer; }
.parcela.clicavel:hover { outline: 2px solid var(--color-accent); outline-offset: 1px; }
.parcela.clicavel:focus-visible { outline: 2px solid var(--color-accent); outline-offset: 1px; }
.p-paga { background: var(--color-success); color: #fff; }
.p-vencida { background: #fee2e2; color: #b91c1c; border-color: var(--color-danger); }
.p-a-vencer { background: #f1f2f4; color: #9ca3af; }
.p-cancelada { background: #fff; color: #d1d5db; border-color: #e5e7eb; text-decoration: line-through; }
/* Recebida por fora continua verde — para "está em dia?" a origem não importa.
   O tracinho responde outra pergunta: de onde veio esse dinheiro. */
.p-manual { box-shadow: inset 0 -3px 0 var(--color-warning); }

.cob-numeros { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; font-size: 13px; color: var(--color-text-secondary); flex: 1; min-width: 0; }
.cob-progresso strong { color: var(--color-text-primary); font-size: 14px; }
.cob-valores { display: flex; gap: 6px; flex-wrap: wrap; }
.v-pago { color: #166534; font-weight: 600; }
.cob-atraso { color: #b91c1c; font-weight: 600; }
.cob-vence { color: var(--color-text-muted); }

.btn-lancar {
  background: none; border: 1px solid var(--color-bg-separator);
  border-radius: 5px; padding: 6px 11px; font-size: 12px; font-weight: 600;
  color: var(--color-text-secondary); cursor: pointer; white-space: nowrap;
  font-family: inherit; margin-left: auto;
}
.btn-lancar:hover { border-color: var(--color-accent); color: var(--color-accent); }

.btn-whatsapp {
  background: #25d366; border: 1px solid #25d366;
  border-radius: 5px; padding: 6px 11px; font-size: 12px; font-weight: 600;
  color: #fff; cursor: pointer; white-space: nowrap; font-family: inherit;
}
.btn-whatsapp:hover { background: #1fb858; }
.btn-whatsapp:disabled { opacity: 0.6; cursor: default; }

.cob-whatsapp-aviso {
  font-size: 11px; color: var(--color-text-secondary);
  border-top: 1px dashed var(--color-bg-separator); padding-top: 6px;
}

.cob-manuais { display: flex; flex-direction: column; gap: 3px; border-top: 1px dashed var(--color-bg-separator); padding-top: 6px; }
.manual-linha { display: flex; flex-wrap: wrap; gap: 5px; font-size: 11px; align-items: baseline; }
.manual-tag { font-weight: 600; color: #92400e; }
.manual-quem { color: var(--color-text-secondary); }
.manual-obs { color: var(--color-text-muted); font-style: italic; }

.chip { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 3px 7px; border-radius: 4px; white-space: nowrap; }
.chip-quitado { background: #dcfce7; color: #166534; }
.chip-atrasado { background: #fee2e2; color: #b91c1c; }
.chip-em-dia { background: #eff6ff; color: #1d4ed8; }

.modal-sub { margin: 0 0 12px; color: var(--color-text-secondary); font-size: var(--text-sm); }
.aviso-manual {
  background: #fef3c7; color: #92400e; border-radius: 6px;
  padding: 10px 12px; font-size: var(--text-sm); margin-bottom: 14px; line-height: 1.4;
}
.escolha-parcelas { display: flex; flex-direction: column; gap: 6px; max-height: 260px; overflow-y: auto; }
.escolha {
  display: flex; align-items: center; gap: 10px; padding: 9px 11px;
  border: 1px solid var(--color-bg-separator); border-radius: 6px; cursor: pointer;
}
.escolha.active { border-color: var(--color-accent); background: #eff6ff; }
.escolha-num { font-weight: 600; font-size: var(--text-sm); }
.escolha-venc { font-size: 12px; color: var(--color-text-secondary); flex: 1; }
.escolha-valor { font-weight: 700; font-size: var(--text-sm); }
.total-lancamento { padding: 10px 0; font-size: var(--text-sm); }

@media (max-width: 1000px) {
  .totais { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 720px) {
  .controles-linha { grid-template-columns: 1fr; }
  /* Quebra em duas linhas em vez de rolar na horizontal: com scroll, "Quitados"
     ficava fora da tela sem nada indicando que existia. */
  .tabs { flex-wrap: wrap; overflow-x: visible; }
  .tab { padding: 7px 10px; }
  /* A ação desce para a própria linha e ocupa a largura toda: alvo de toque
     confortável, em vez de um botão de 12px encostado na borda. */
  .cob-corpo { align-items: flex-start; }
  .btn-lancar { margin-left: 0; width: 100%; padding: 9px; }
  .btn-whatsapp { margin-left: 0; width: 100%; padding: 9px; }
  .cob-numeros { flex: 1 1 100%; }
}
@media (max-width: 380px) {
  .parcela { width: 24px; height: 24px; }
  .total-card { padding: 10px 11px; }
  .total-valor { font-size: 16px; }
}
</style>
